import React from 'react'
import { TEXT_KEYS } from '../../constants/textKeys'
import { t } from '../../translation/translation'
import './style.css'

export const AppPermission = () => {
    return (
        <div className="page-container app-permission-container">
            <header className="app-permission-header">
                <h2>{t(TEXT_KEYS.APP_PERMISSIONS_TITLE)}</h2>
                <p className="app-permission-lead">
                    This page documents SoApp privacy practices (for end users) and explains why the mobile
                    apps request certain OS permissions/capabilities (for reviewers).
                </p>
                <div className="app-permission-meta">
                    <p><b>Effective date:</b> 2026-04-01</p>
                    <p><b>Last updated:</b> 2026-05-07</p>
                </div>
            </header>

            <div className="app-permission-card box-with-shadow app-permission-content">
                <p>This page serves two purposes:</p>
                <ul>
                    <li><b>Privacy Policy (end users):</b> what data SoApp collects, why, how it’s used/shared, and your choices.</li>
                    <li><b>Permissions rationale (reviewers):</b> why the mobile apps request certain OS permissions/capabilities.</li>
                </ul>
                <p>If you find a mismatch between this page and the shipped apps, <b>the shipped app configuration is authoritative</b>; please open an issue/PR so we can align this page.</p>

                <h4>1) Who we are (data controller) & contact</h4>
                <p>SoApp is provided by <b>SoApp Group</b> (the “Company”, “we”, “us”). We act as the data controller for personal data processed in SoApp unless you are using SoApp under an enterprise contract where your organization acts as controller and we act as processor.</p>
                <ul>
                    <li><b>Contact email:</b> <i>privacy@your-domain.com</i></li>
                    <li><b>Company address (optional):</b> <i>Your address</i></li>
                </ul>
            </div>

            <div className="app-permission-card box-with-shadow app-permission-content">
                <h3>Privacy policy overview</h3>

                <h4>2) What data we collect</h4>
                <p>Depending on how you use SoApp, we may process the following categories of information:</p>
                <ul>
                    <li><b>Account and profile data</b>: name/username, profile picture, email/phone (if you sign up with them), preferences.</li>
                    <li><b>User-generated content</b>: messages, attachments you send (photos/videos/audio/files), and metadata needed to deliver them (timestamps, recipients).</li>
                    <li><b>Device and app information</b>: device model, OS version, app version, language, identifiers used for push notifications (e.g., Firebase token), and diagnostics.</li>
                    <li><b>Usage data</b>: feature usage, interactions, in-app events (if analytics is enabled in the build you use).</li>
                    <li><b>Approximate or precise location</b> (only if you enable/trigger a feature that requests it): we only request location when a feature needs it and the OS asks you for permission.</li>
                </ul>
                <p><b>We do not intentionally collect sensitive personal data</b> (e.g., health, biometrics) unless you choose to provide it via content you upload.</p>

                <h4>3) Why we use your data (purposes)</h4>
                <p>We use personal data to:</p>
                <ul>
                    <li><b>Provide the core service</b>: create accounts, authenticate users, deliver messages, sync content across devices.</li>
                    <li><b>Enable requested features</b>: for example camera attachments, voice messages, media picking, notifications.</li>
                    <li><b>Maintain, secure, and improve SoApp</b>: debugging, crash diagnostics, performance monitoring, abuse prevention, and security.</li>
                    <li><b>Comply with legal obligations</b>: where required by law, regulation, or valid legal process.</li>
                </ul>

                <h4>4) Legal bases (where applicable)</h4>
                <p>Where privacy laws apply (e.g., GDPR/UK GDPR), we process data under one or more of the following legal bases:</p>
                <ul>
                    <li><b>Contract</b>: to provide the service you request.</li>
                    <li><b>Legitimate interests</b>: service security, fraud prevention, and improvements (balanced against your rights).</li>
                    <li><b>Consent</b>: for optional features or where the OS requires permission (camera/microphone/photos/notifications/location).</li>
                    <li><b>Legal obligation</b>: to comply with applicable laws.</li>
                </ul>
            </div>

            <div className="app-permission-card box-with-shadow app-permission-content">
                <h4>5) Sharing and disclosure</h4>
                <p>We may share information in these limited cases:</p>
                <ul>
                    <li><b>Service providers (processors)</b>: cloud hosting, push notifications, crash reporting/analytics, email/SMS delivery. They are permitted to process data only on our instructions and for the purposes described here.</li>
                    <li><b>Legal and safety</b>: if required to comply with law or to protect users, the public, or our services.</li>
                    <li><b>Business transfers</b>: if we’re involved in a merger, acquisition, financing, or sale of assets, data may be transferred subject to this policy.</li>
                </ul>
                <p><b>We do not sell your personal information.</b></p>

                <h4>6) Data retention</h4>
                <p>We keep data only as long as necessary for the purposes described:</p>
                <ul>
                    <li><b>Account data</b>: retained while your account is active and as needed for legitimate business purposes.</li>
                    <li><b>Messages and attachments</b>: retained to provide the messaging service, unless you delete content (where supported) or close your account.</li>
                    <li><b>Diagnostics/logs</b>: typically retained for shorter periods to troubleshoot and secure the service.</li>
                </ul>
                <p>Exact retention can vary depending on feature use, backups, and legal requirements.</p>

                <h4>7) Security</h4>
                <p>We use reasonable administrative, technical, and organizational measures to protect data. However, no method of transmission or storage is 100% secure.</p>

                <h4>8) Your choices and rights</h4>
                <p>Depending on your region, you may have rights such as access, correction, deletion, objection, restriction, and portability.</p>
                <ul>
                    <li><b>In-app permissions</b>: you can grant/deny camera, microphone, photos, notifications, and location via your device settings.</li>
                    <li><b>Account controls</b>: you can update profile settings in the app (where available).</li>
                    <li><b>Request help</b>: contact us at the email above to request access/deletion or other rights.</li>
                </ul>

                <h4>9) Children’s privacy</h4>
                <p>SoApp is not directed to children under the age required by local law to consent to data processing. If you believe a child has provided personal data, contact us and we will take appropriate steps.</p>

                <h4>10) International transfers</h4>
                <p>If we transfer data across borders, we use appropriate safeguards as required by law (for example contractual protections).</p>

                <h4>11) Changes to this policy</h4>
                <p>We may update this policy from time to time. We will change the “Last updated” date and, where appropriate, provide additional notice in the app or on our website.</p>
            </div>

            <div className="app-permission-card box-with-shadow app-permission-content">
                <h3>Permissions rationale</h3>
                <h4>Android</h4>
                <div className="app-permission-table-wrap">
                    <table className='app-permission-table'>
                        <thead>
                            <tr>
                                <th>Permission / capability</th>
                                <th>Why we need it</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><b>INTERNET</b></td>
                                <td>Core networking: sign-in, messaging, media, GraphQL/API traffic, and other online features.</td>
                            </tr>
                            <tr>
                                <td><b>RECORD_AUDIO</b></td>
                                <td>Voice messages, audio capture, or calls where the user explicitly records or sends audio.</td>
                            </tr>
                            <tr>
                                <td><b>CAMERA</b></td>
                                <td>Taking photos or videos from the camera (e.g. chat attachments, profile or content capture).</td>
                            </tr>
                            <tr>
                                <td><b>READ_MEDIA_IMAGES</b> / <b>READ_MEDIA_VIDEO</b></td>
                                <td>On modern Android versions, picking or attaching <b>photos and videos</b> from the device without broad file access.</td>
                            </tr>
                            <tr>
                                <td><b>READ_EXTERNAL_STORAGE</b> / <b>WRITE_EXTERNAL_STORAGE</b></td>
                                <td>Legacy and compatibility paths for reading/writing media or exports on older Android versions (newer versions use scoped storage and granular media permissions where possible).</td>
                            </tr>
                            <tr>
                                <td><b>VIBRATE</b></td>
                                <td>Haptic feedback for notifications, actions, or UI events.</td>
                            </tr>
                            <tr>
                                <td><b>POST_NOTIFICATIONS</b></td>
                                <td>Showing <b>push notifications</b> (Android 13+) so users can be alerted about messages and activity when the app is in the background.</td>
                            </tr>
                            <tr>
                                <td><b>RECEIVE_BOOT_COMPLETED</b></td>
                                <td>Typical for <b>notification delivery and scheduling</b> after the device restarts (exact behavior depends on libraries such as Firebase).</td>
                            </tr>
                            <tr>
                                <td><b>FOREGROUND_SERVICE</b></td>
                                <td>Allows a <b>foreground service</b> when the OS requires a visible ongoing task (for example long-running audio, uploads, or other work that must not be killed silently).</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="app-permission-card box-with-shadow app-permission-content">
                <h4>Package visibility (<code>{'<queries>'}</code>)</h4>
                <p>Android 11+ limits which installed apps your app can “see” without declaring interest. SoApp declares a set of <b>well-known third-party package names</b> so the app can safely check whether those apps are installed or open/share content via intents. This is <b>not</b> access to your data inside those apps; it only affects whether the OS allows SoApp to resolve those intents.</p>
            </div>

            <div className="app-permission-card box-with-shadow app-permission-content">
                <h4>iOS</h4>
                <p>Usage descriptions are defined in <code>apps/mobile/ios/SoApp/Info.plist</code>:</p>
                <div className="app-permission-table-wrap">
                    <table className='app-permission-table'>
                        <thead>
                            <tr>
                                <th>Key</th>
                                <th>Why we need it</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><b>NSCameraUsageDescription</b></td>
                                <td>Access the camera to take photos and videos the user chooses to capture or send.</td>
                            </tr>
                            <tr>
                                <td><b>NSMicrophoneUsageDescription</b></td>
                                <td>Access the microphone for voice recording or calls; audio is not shared beyond what the user sends in the product.</td>
                            </tr>
                            <tr>
                                <td><b>NSPhotoLibraryUsageDescription</b></td>
                                <td>Read the photo library so the user can <b>pick</b> photos and videos to attach or upload.</td>
                            </tr>
                            <tr>
                                <td><b>NSLocationWhenInUseUsageDescription</b></td>
                                <td>Present for <b>when-in-use</b> location if a flow requests it (e.g. optional maps or venue-related features).</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="app-permission-card box-with-shadow app-permission-content">
                <h4>Third‑party SDKs and store disclosures</h4>
                <p>SoApp may use third‑party SDKs (for example for push notifications, analytics, or crash reporting). Those SDKs may process limited device and usage information to provide their services. You should ensure that:</p>
                <ul>
                    <li>Your <b>Google Play Data safety</b> form matches what your shipped build does.</li>
                    <li>Your <b>App Store privacy nutrition labels</b> match what your shipped build does.</li>
                </ul>

                <h4>Keeping this accurate</h4>
                <p>When adding a permission or usage description:</p>
                <ol>
                    <li>Update the manifest or <code>Info.plist</code>.</li>
                    <li>Update this page in the same change when the reason is not obvious from the feature name alone.</li>
                </ol>
            </div>
        </div>
    )
}

export default AppPermission
